import React, { useCallback, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Star, ArrowRight, Trophy, Medal, Award, ThumbsUp, MessageSquare } from 'lucide-react';
import { PageLayout } from '../../components/styled/PageLayout';
import { Container } from '../../components/styled/Container';
import SEO from '../../components/seo/SEO';
import { fetchTopCatalog } from '../../store/actions/topActions';
import { regionFromPathname } from '../../utils/regionFromPath';
import { serverLanguageFromRegion } from '../../utils/serverLanguage';
import { detailPath } from '../../utils/paths';
import { useI18n } from '../../hooks/useI18n';
import StaticAdBanner from '../../components/ads/StaticAdBanner';
import AdInline from '../../components/ads/AdInline';
import { bannerAds, cardAds } from '../../data/staticAds';
import { useFetchManagedPlacements } from '../../hooks/useFetchManagedPlacements';
import { useSiteAdsManaged } from '../../hooks/useSiteAdsManaged';
import { trackAdClick, trackAdImpression } from '../../store/actions/adActions';
import { selectAdPlacementFirst } from '../../store/selectors/adSelectors';
import { managedPlacementToAd, pickStaticFallback } from '../../utils/adMapper';
import { selectAdsStaticEnabled } from '../../store/reducers/sitePublicReducer';
import { makeImpressionKey, shouldTrackImpression } from '../../utils/adTracking';

const TOP_AD_REQ = Object.freeze([
  { placement: 'inline-banner', limit: 1 },
  { placement: 'results-inline', limit: 1 },
]);

const Head = styled.div`
  margin-bottom: 1.25rem;
`;

const Title = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
`;

const Sub = styled.p`
  margin: 0.5rem 0 0 0;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
`;

const TopThree = styled.div`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Spotlight = styled.article`
  grid-column: ${({ $rank }) => ($rank === 1 ? 'span 6' : 'span 3')};
  background: ${({ $rank }) =>
    $rank === 1
      ? 'linear-gradient(145deg, rgba(124,58,237,0.15), rgba(245,158,11,0.1))'
      : 'linear-gradient(145deg, rgba(0,48,135,0.08), rgba(124,58,237,0.08))'};
  border: 1px solid rgba(124, 58, 237, 0.15);
  border-radius: ${({ theme }) => theme.radiusMd};
  box-shadow: ${({ theme }) => theme.shadowCard};
  padding: ${({ $rank }) => ($rank === 1 ? '1rem' : '0.9rem')};
  display: flex;
  gap: 0.85rem;
`;

const Card = styled.article`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  padding: 1rem;
  display: flex;
  gap: 1rem;
`;

const Poster = styled.div`
  width: ${({ $large }) => ($large ? '94px' : '64px')};
  height: ${({ $large }) => ($large ? '140px' : '96px')};
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`;

const ItemTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontClamp.lg};
`;

const Muted = styled.p`
  margin: 0.35rem 0 0 0;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
`;

const RankPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ $rank }) => ($rank === 1 ? '#b45309' : $rank === 2 ? '#475569' : '#7c2d12')};
`;

const Stats = styled.div`
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
  margin-top: 0.4rem;
`;

const StatBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  background: rgba(2, 6, 23, 0.06);
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Open = styled(Link)`
  margin-top: 0.6rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  text-decoration: none;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;

function titleFor(type, t) {
  if (type === 'movies') return t('top.movies.title', 'Top movie matches');
  if (type === 'tv') return t('top.tv.title', 'Top TV matches');
  if (type === 'anime-movies') return t('top.animeMovies.title', 'Top anime movie matches');
  if (type === 'anime-tv') return t('top.animeTv.title', 'Top anime TV matches');
  return t('top.anime.title', 'Top anime matches');
}

function groupFor(type) {
  if (type === 'movies') return 'movies';
  if (type === 'tv') return 'tv';
  if (type === 'anime-movies') return 'anime_movie';
  if (type === 'anime-tv') return 'anime_tv';
  return 'anime';
}

function typeLabel(category) {
  if (category === 'anime_movie') return 'Anime movie';
  if (category === 'anime_tv') return 'Anime TV series';
  if (category === 'tv') return 'TV series';
  return 'Movie';
}

export default function TopMatchesPage({ type }) {
  const dispatch = useDispatch();
  const { sitePublicLoaded, adsManagedEnabled } = useSiteAdsManaged();
  const adsStaticEnabled = useSelector(selectAdsStaticEnabled);
  const { pathname } = useLocation();
  const region = regionFromPathname(pathname);
  const serverLanguage = serverLanguageFromRegion(region);
  const { t } = useI18n();
  const group = groupFor(type);
  const { items, loading } = useSelector((s) => s.top);

  useEffect(() => {
    dispatch(fetchTopCatalog({ group, limit: 90, language: serverLanguage }));
  }, [dispatch, group, serverLanguage]);

  const top = useMemo(() => {
    return (Array.isArray(items) ? items : []).slice(0, 60);
  }, [items]);
  const topThree = top.slice(0, 3);
  const rest = top.slice(3);

  const { adDomain, adLocale, adDevice } = useFetchManagedPlacements('top-sites', TOP_AD_REQ);
  const managedTopBanner = useSelector((s) => selectAdPlacementFirst(s, 'top-sites', 'inline-banner'));
  const managedTopInline = useSelector((s) => selectAdPlacementFirst(s, 'top-sites', 'results-inline'));

  const topBannerAd = useMemo(
    () =>
      managedPlacementToAd(managedTopBanner, pickStaticFallback(adsStaticEnabled, bannerAds[1] ?? bannerAds[0])),
    [managedTopBanner, adsStaticEnabled],
  );
  const topInlineAd = useMemo(
    () => managedPlacementToAd(managedTopInline, pickStaticFallback(adsStaticEnabled, cardAds[0])),
    [managedTopInline, adsStaticEnabled],
  );

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedTopBanner?._id) return;
    const key = makeImpressionKey({
      campaign: managedTopBanner._id,
      page: 'top-sites',
      placement: 'inline-banner',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedTopBanner._id,
          page: 'top-sites',
          placement: 'inline-banner',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedTopBanner, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedTopInline?._id) return;
    const key = makeImpressionKey({
      campaign: managedTopInline._id,
      page: 'top-sites',
      placement: 'results-inline',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedTopInline._id,
          page: 'top-sites',
          placement: 'results-inline',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedTopInline, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  const onTopBannerClick = useCallback(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedTopBanner?._id) return;
    dispatch(
      trackAdClick({
        campaign: managedTopBanner._id,
        page: 'top-sites',
        placement: 'inline-banner',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managedTopBanner, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  const onTopInlineClick = useCallback(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedTopInline?._id) return;
    dispatch(
      trackAdClick({
        campaign: managedTopInline._id,
        page: 'top-sites',
        placement: 'results-inline',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managedTopInline, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  function rankIcon(rank) {
    if (rank === 1) return <Trophy size={13} />;
    if (rank === 2) return <Medal size={13} />;
    return <Award size={13} />;
  }

  function formatScore(n, digits = 2) {
    const v = Number(n);
    if (!Number.isFinite(v)) return '—';
    return v.toFixed(digits);
  }

  return (
    <>
      <SEO
        region={region}
        path={`/top/${type}`}
        title={`${titleFor(type, t)} | FliqMatch`}
        description={t('top.meta.description', 'Browse top matches ranked from curated picks and community activity.')}
      />
      <PageLayout>
        <Container style={{ paddingTop: '2.25rem', paddingBottom: '2.25rem' }}>
          <Head>
            <Title>{titleFor(type, t)}</Title>
            <Sub>
              {t('top.subtitle', 'Rankings combine curation quality and ongoing community votes/reviews.')}
            </Sub>
          </Head>
          <div style={{ margin: '1.25rem 0' }}>
            <StaticAdBanner ad={topBannerAd} variant="inline" pageKey="top-sites" onLinkClick={onTopBannerClick} />
          </div>
          <AdInline ad={topInlineAd} pageKey="top-sites" onLinkClick={onTopInlineClick} />
          {loading && !top.length ? <Muted>{t('common.loading', 'Loading...')}</Muted> : null}
          {!loading && !top.length ? <Muted>{t('top.empty', 'No titles found yet.')}</Muted> : null}
          <TopThree>
            {topThree.map((m, idx) => {
              const rank = idx + 1;
              return (
                <Spotlight key={`spot-${m.category}-${m.tmdbId}`} $rank={rank}>
                  <Poster $url={m.poster} $large={rank === 1} />
                  <div style={{ minWidth: 0 }}>
                    <RankPill $rank={rank}>
                      {rankIcon(rank)} #{rank}
                    </RankPill>
                    <ItemTitle>{m.title}</ItemTitle>
                    <Muted>{m.overview || t('top.cardFallback', 'Open this title to see similar picks and community insights.')}</Muted>
                    <Stats>
                      <StatBadge>{typeLabel(m.category)}</StatBadge>
                      <StatBadge>
                        <Star size={12} /> {formatScore(m?.score?.topScore)} top
                      </StatBadge>
                      <StatBadge>
                        <ThumbsUp size={12} /> {formatScore(m?.score?.voteAvg)} ({m?.score?.voteCount || 0})
                      </StatBadge>
                      <StatBadge>
                        <MessageSquare size={12} /> {formatScore(m?.score?.reviewAvg)} ({m?.score?.reviewCount || 0})
                      </StatBadge>
                    </Stats>
                    <Open to={detailPath(m.category, m.tmdbId, m.title, region)}>
                      {t('top.viewDetails', 'View details')} <ArrowRight size={14} />
                    </Open>
                  </div>
                </Spotlight>
              );
            })}
          </TopThree>
          <Grid>
            {rest.map((m, idx) => (
              <Card key={`${m.category}-${m.tmdbId}`}>
                <Poster $url={m.poster} />
                <div style={{ minWidth: 0 }}>
                  <Muted style={{ margin: 0 }}>
                    <Star size={12} style={{ verticalAlign: '-2px' }} /> #{idx + 4}
                  </Muted>
                  <ItemTitle>{m.title}</ItemTitle>
                  <Muted>{m.overview || t('top.cardFallback', 'Open this title to see similar picks and community insights.')}</Muted>
                  <Stats>
                    <StatBadge>{typeLabel(m.category)}</StatBadge>
                    <StatBadge>
                      <Star size={12} /> {formatScore(m?.score?.topScore)} top
                    </StatBadge>
                    <StatBadge>
                      <ThumbsUp size={12} /> {formatScore(m?.score?.voteAvg)} ({m?.score?.voteCount || 0})
                    </StatBadge>
                    <StatBadge>
                      <MessageSquare size={12} /> {formatScore(m?.score?.reviewAvg)} ({m?.score?.reviewCount || 0})
                    </StatBadge>
                  </Stats>
                  <Open to={detailPath(m.category, m.tmdbId, m.title, region)}>
                    {t('top.viewDetails', 'View details')} <ArrowRight size={14} />
                  </Open>
                </div>
              </Card>
            ))}
          </Grid>
        </Container>
      </PageLayout>
    </>
  );
}
